import { Bold } from '@lyearn/icons';
import { BaseExtension, EditorView, NewCoreEditorProps, NewEditorProps } from '@lyearn/lygo-core';
import { Uppy } from '@lyearn/shared/hooks/useUppy';
import { DeprecatedIconButtonVariantProps } from '@lyearn/ui';
import { TypographyColors, TypographyVariants } from '@lyearn/ui';
import { IconButtonVariant } from '@lyearn/ui/dist/esm/Buttons/IconButton/types';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import { MentionInputType } from '@/types/schema';

export type EditorRefType = { editorView?: EditorView };

export interface UseEditorInputProps {
  value: string;
  medias: EntityMediaFragment[];
  onEditorBlur?: NewCoreEditorProps['onBlur'];
  onChangeText: (inputHtml: string) => void;
  placeholder: string;
  onChangeMedias?: (medias: EntityMediaFragment[]) => void;
  onChangeMentions?: (mentions: MentionInputType[]) => void;
  charLimit?: number;
  extensions?: { new (): BaseExtension }[];
  plugins?: BaseExtension['plugins'][];
  enableMentions?: boolean;
  editorRef?: React.RefObject<EditorRefType>;
  disabled?: boolean;
  readOnly?: boolean;
  yProvider?: NewCoreEditorProps['yProvider'];
}

export interface EditorContextProps {
  editorProps: NewEditorProps;
  uppy: Uppy | null;
  onEditorOptionClick: (blockName: string, blockType: EditorOption) => void;
  mediaContainerProps?: {
    onRemoveMedia: (media: EntityMediaFragment) => void;
    medias: EntityMediaFragment[];
    disabled?: boolean;
  };
  marksActiveMap: Record<string, MarkState>;
  editorView?: EditorView;
  onEditorFocus: (event: MouseEvent) => void;
}

export interface EditorProps {
  value?: string | null;
  placeholder?: string;
  onChangeText: (inputHtml: string) => void;
  onEditorBlur?: NewCoreEditorProps['onBlur'];
  className?: string;
  medias?: EntityMediaFragment[];
  onChangeMedias?: (medias: EntityMediaFragment[]) => void;
  onChangeMentions?: (mentions: MentionInputType[]) => void;
  charLimit?: number;
  extensions?: { new (): BaseExtension }[];
  plugins?: BaseExtension['plugins'][];
  enableMentions?: boolean;
  editorRef?: React.RefObject<EditorRefType>;
  disabled?: boolean;
  readOnly?: boolean;
  yProvider?: NewCoreEditorProps['yProvider'];
}

export interface ToolbarProps {
  isMedia?: boolean;
  classes?: {
    root?: string;
    container?: string;
  };
  toolbarContainerClassName?: string;
  hideDivider?: boolean;
  editorOptions?: (ToolbarOption | string)[];
  editorOptionMap?: Map<ToolbarOption | string, EditorOptionType>;
  disabled?: boolean;
}

export interface SeparatorProps {
  className?: string;
}

export interface AttachmentContainerProps {
  className?: string;
  disabled?: boolean;
}

export type ToolbarButtonProps = DeprecatedIconButtonVariantProps & {
  accept?: string;
  tooltipKey?: string;
  Icon: (props: { className?: string }) => JSX.Element;
  editorOption?: EditorOption;
  blockName?: string;
  className?: string;
  classes?: {
    [key: string]: string;
  };
  isActive?: boolean;
  isHidden?: boolean;
  onClick: (blockName: string, blockType: EditorOption) => void;
  uppy?: Uppy | null;
  editorView?: EditorView;
  variant?: IconButtonVariant;
};

export enum EditorOption {
  List = 'LIST',
  Mark = 'MARK',
}

export enum MarkState {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Hidden = 'Hidden',
}

export interface PlainEditorProps {
  value?: string | null;
  onChangeText: (inputHtml: string) => void;
  onEditorBlur?: NewCoreEditorProps['onBlur'];
  placeholder?: string;
  variant?: TypographyVariants;
  color?: TypographyColors;
  className?: string;
  classes?: {
    editor?: string;
    toolbar?: ToolbarProps['classes'];
  };
  medias?: EntityMediaFragment[];
  onChangeMedias?: (medias: EntityMediaFragment[]) => void;
  charLimit?: number;
  editorOptions?: ToolbarProps['editorOptions'];
  editorOptionMap?: ToolbarProps['editorOptionMap'];
  editorCoreClasses?: { typography?: string; contentEditableContainer?: string };
  disableSelection?: boolean;
  showEditor?: boolean;
  showToolbar?: boolean;
  editorProps?: Pick<EditorProps, 'plugins' | 'extensions' | 'readOnly' | 'yProvider'>;
  children?: React.ReactNode;
  disabled?: boolean;
  hideToolbarDivider?: boolean;
  attachmentPosition?: 'top' | 'bottom';
}

export enum ToolbarOption {
  Bold = 'Bold',
  Italic = 'Italic',
  Underline = 'Underline',
  Strikethrough = 'StrikeThrough',
  Link = 'link',
  NumberedList = 'NumberedList',
  BulletList = 'BulletList',
  Separator = 'Separator',
  Spacer = 'Spacer',
  AttachFile = 'AttachFile',
  AttachMedia = 'AttachMedia',
  Code = 'Code',
}

export type EditorOptionType = {
  Component: React.FC<ToolbarButtonProps>;
  key: string;
  Icon: typeof Bold;
  blockName: string;
  className?: string;
  classes?: {
    [key: string]: string;
  };
  editorOption?: EditorOption;
  tooltipKey?: string;
  accept?: string;
};
